import { useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Collapse,
  IconButton,
  Link,
  Chip,
  Divider,
} from "@mui/material";
import {
  Briefcase,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  MemoryStick,
  X,
  OctagonX,
  AlarmClock,
  Trash2,
  PartyPopper,
  House,
  BookUser,
  ExternalLink,
  CircleOff,
  Ghost,
  ListTodo,
  Clock,
} from "lucide-react";
import useIsMobile from "../../components/hooks/useIsMobile";
import LinkRouter from "../../components/navigation/LinkRouter";
import DateDisplay from "../../components/common/DateDisplay";
import { SignupSubmission } from "../../protogen/signup_service_pb";
import MetadataSection from "../../components/common/MetadataSection";
import AddFamilyDialog from "../family/AddFamilyDialog";
import ManualAdvisorAssignDialog from "./ManualAdvisorAssignDialog";
import { leadToFamily, emptyUpdates, normalizeLeadState } from "./utils";
import { Family, Member } from "../family/types";
import {
  useUpdateSubmission,
  useGetSignupPaymentLink,
} from "../../services/signup";
import {
  absoluteTime,
  formatUSPhoneNumber,
  pluralize,
} from "../../common/utils";
import { format } from "date-fns";
import DropdownMenu from "../common/DropdownMenu";
import CopyButton from "../common/CopyButton";
import LeadFeed from "./LeadFeed";
import { useNavigate } from "react-router-dom";
import { MembershipOption } from "../signup/types";
import MembershipSelectionModal from "./MembershipSelectionModal";
import { Membership } from "../../protogen/signup_service_pb";
import {
  BasicDetail,
  ContactDetail,
  MultiSelectDetail,
  TrackingDetails,
} from "./Details";
import { AccountType } from "../../protogen/common_pb";

const leftFlex = 3;
const rightFlex = 5;

const getStatus = (lead: SignupSubmission) => {
  const StatusChip = ({
    label,
    subtext,
    bgColor,
    textColor,
    subtextColor,
    subtextBgColor,
  }: {
    label: string;
    subtext?: string;
    bgColor: string;
    textColor: string;
    subtextColor?: string;
    subtextBgColor?: string;
  }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Chip
        label={label}
        variant="filled"
        sx={{ background: bgColor, color: textColor, width: "125px" }}
      />
      {subtext && (
        <Chip
          label={subtext}
          variant={subtextBgColor ? "filled" : "outlined"}
          sx={{ background: subtextBgColor, color: subtextColor }}
        />
      )}
    </Box>
  );

  const MatchmakingChip = ({
    title,
    lead,
  }: {
    title: string;
    lead: SignupSubmission;
  }) => {
    // "active-waiting", "queued", "declined", "timed-out", "accepted"
    const pending = lead.assignments?.filter(
      (a) => a.status === "active-waiting" || a.status === "queued",
    );
    const declined = lead.assignments?.filter(
      (a) => a.status === "declined" || a.status === "timed-out",
    );
    let actionable = false;
    let subtext = "";
    const accepted = lead.assignments?.filter((a) => a.status === "accepted");
    if (pending.length + declined.length + accepted.length === 0) {
      actionable = true;
      subtext = "Needs matchmaking";
    } else if (accepted.length > 0) {
      actionable = true;
      subtext = `Accepted`;
    } else if (pending.length === 0) {
      actionable = true;
      subtext = `Declined (${declined.length})`;
    } else if (declined.length === 0) {
      actionable = false;
      subtext = `${pending.length} Pending`;
    } else {
      actionable = false;
      subtext = `${pending.length} Pending, ${declined.length} Declined`;
    }
    return (
      <LinkRouter
        stopClickPropagation={true}
        to={`/tools/matchmaking/${encodeURIComponent(lead.ref)}`}
        sx={{ textDecoration: "none" }}
      >
        <StatusChip
          label={title}
          bgColor="#FFE0E0"
          textColor={"#910838"}
          subtext={subtext}
          subtextColor={actionable ? "#8E4415" : undefined}
          subtextBgColor={actionable ? "#FAE5D3" : undefined}
        />
      </LinkRouter>
    );
  };

  switch (lead.pipelineStatus.toLowerCase()) {
    case "signup-complete":
    case "awaiting-match":
      return <MatchmakingChip title="Signup" lead={lead} />;

    case "consultation-cancelled":
      return (
        <StatusChip
          label="Consultation"
          bgColor="#F5F5F5"
          textColor={"#6B6E7B"}
          subtext={"Canceled"}
        />
      );
    case "no-show":
      return (
        <StatusChip
          label="Consultation"
          bgColor="#F5F5F5"
          textColor={"#6B6E7B"}
          subtext={"No show"}
        />
      );
    case "consultation-scheduled":
      const showConsultation =
        lead.state?.consultationEventStartSec &&
        lead.state?.consultationEventStartSec > new Date().getTime() / 1000;
      const consultDate = new Date(
        Number(lead.state?.consultationEventStartSec) * 1000,
      );
      return (
        <StatusChip
          label="Consultation"
          bgColor={showConsultation ? "#EEF3FC" : "rgba(251, 242, 229, 0.95)"}
          textColor={showConsultation ? "#3061AF" : "#9A6518"}
          subtext={
            showConsultation ? `${format(consultDate, "MMM d")}` : "Completed"
          }
        />
      );
    case "converted":
      return (
        <LinkRouter
          stopClickPropagation={true}
          to={`/tools/matchmaking/${encodeURIComponent(lead.ref)}`}
          sx={{ textDecoration: "none" }}
        >
          <StatusChip
            label="Converted"
            bgColor="#ECF4EC"
            textColor={"#198282"}
          />
        </LinkRouter>
      );
    case "declined":
      return (
        <StatusChip label="Declined" bgColor="#F5F5F5" textColor={"#6B6E7B"} />
      );
    case "verbal-yes":
      return (
        <StatusChip
          label="Verbal yes"
          bgColor="#F5F5F5"
          textColor={"#6B6E7B"}
        />
      );
    case "consultation-non-conversion":
      return (
        <StatusChip
          label="Consultation non-conversion"
          bgColor="#F5F5F5"
          textColor={"#6B6E7B"}
        />
      );
    case "maybe-later":
      return (
        <StatusChip
          label="Maybe later"
          bgColor="#F5F5F5"
          textColor={"#6B6E7B"}
        />
      );
    case "deleted":
      return (
        <StatusChip label="Deleted" bgColor="#F5F5F5" textColor={"#6B6E7B"} />
      );
    case "account-creation-complete":
      return (
        <StatusChip
          label="Unpaid Account Creation"
          bgColor="#F5F5F5"
          textColor={"#6B6E7B"}
        />
      );
    case "get-started-digital-subscription":
      return (
        <StatusChip
          label="Digital Subscription"
          bgColor="#EDE9FE"
          textColor={"#6D28D9"}
        />
      );
    default:
      return (
        <StatusChip
          label={lead.pipelineStatus.toLowerCase()}
          bgColor="#FFF"
          textColor={"#6B6E7B"}
        />
      );
  }
};

const LeadAnswers = ({ lead }: { lead: SignupSubmission }) => {
  const normalized = normalizeLeadState(lead);
  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: "24px",
        borderTop: "1px solid #E2E3E4",
        padding: isMobile ? "20px 0 0" : "20px 0",
      }}
    >
      {/* Left */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: leftFlex,
          gap: "20px",
        }}
      >
        <MultiSelectDetail
          label={"Goals"}
          Icon={CheckCircle}
          items={normalized.goals}
        />
        <MultiSelectDetail
          label={"Household"}
          Icon={Briefcase}
          items={normalized.household}
        />
        <MultiSelectDetail
          label={"Superpowers"}
          Icon={MemoryStick}
          items={normalized.superpowers}
        />
        <MultiSelectDetail
          label={"Life events"}
          Icon={House}
          items={normalized.lifeEvents}
        />
        <MultiSelectDetail
          label={"Fun"}
          Icon={PartyPopper}
          items={normalized.fun}
        />
        <MultiSelectDetail
          label={"Initial Tasks"}
          Icon={ListTodo}
          items={normalized.initialTasks}
        />
      </Box>

      {/* Right */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: rightFlex,
          gap: "16px",
        }}
      >
        <BasicDetail label={"Family Info"} content={lead?.state?.familyInfo} />
        <BasicDetail
          label={"Anything else"}
          content={lead?.state?.anythingElse}
          alwaysShow={true}
        />
        <BasicDetail
          label={"Referral source"}
          content={lead?.state?.referralSource}
          alwaysShow={true}
        />

        <CopyButton
          variant="text"
          label="Copy summary"
          value={lead.blurb}
          sx={{ alignSelf: "start" }}
        />
      </Box>
    </Box>
  );
};

const LeadDetails = ({
  lead,
  dateType,
}: {
  lead: SignupSubmission;
  dateType?: "completion" | "lastActivity";
}) => {
  const isMobile = useIsMobile();
  const normalized = normalizeLeadState(lead);
  return (
    <Box sx={{ padding: "0", flex: leftFlex }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap="10px"
      >
        {isMobile && (
          <Typography
            variant="body"
            sx={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            {/* Formatted date */}
            {dateType === "completion" ? (
              <DateDisplay date={new Date(Number(lead.completionSec) * 1000)} />
            ) : (
              <DateDisplay
                date={new Date(Number(lead.lastActivitySec) * 1000)}
              />
            )}
          </Typography>
        )}

        {/* Left */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Family link */}
          {lead.family && (
            <Typography variant="body">
              <LinkRouter
                sx={{ color: "#198282" }}
                to={
                  lead.pipelineStatus === "get-started-digital-subscription"
                    ? `/tools/bot-conversations?familyRef=${encodeURIComponent(
                        lead.family?.ref,
                      )}`
                    : `/families/${encodeURIComponent(
                        lead.family?.ref,
                      )}#tab-admin`
                }
              >
                {lead.family?.name} Family
              </LinkRouter>
            </Typography>
          )}

          {/* Contact info */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography variant="bodyHeavy">Contact info</Typography>
            {normalized.phone && (
              <ContactDetail
                icon={"Phone"}
                content={
                  <Link
                    href={`/inbox/phone/create?phone=${encodeURIComponent(
                      normalized.phone || "",
                    )}`}
                    sx={{ fontWeight: 500 }}
                  >
                    {formatUSPhoneNumber(normalized.phone)}
                  </Link>
                }
              />
            )}
            {normalized.email && (
              <ContactDetail
                icon={"Mail"}
                content={
                  <Link
                    href={`/inbox/email/create?email=${encodeURIComponent(
                      normalized.email || "",
                    )}`}
                    sx={{ fontWeight: 500 }}
                  >
                    {normalized.email}
                  </Link>
                }
              />
            )}
            {normalized.location && (
              <ContactDetail
                icon={"MapPin"}
                content={
                  <Link
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      normalized.location,
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: 500 }}
                  >
                    {normalized.location}
                  </Link>
                }
              />
            )}
          </Box>

          <TrackingDetails trackingParameters={lead.trackingParameters} />
        </Box>
      </Box>
    </Box>
  );
};

const LeadMetadata = ({ lead }: { lead: SignupSubmission }) => {
  const { request } = useUpdateSubmission();

  if (lead.metadata === undefined) return null;
  return (
    <MetadataSection
      metadata={lead.metadata}
      saveMetadata={async (metadata) => {
        const data = emptyUpdates(lead.ref);
        data.shouldUpdateMetadata = true;
        data.updatedMetadata = metadata;
        await request(data);
      }}
      entityType={"family"}
    />
  );
};

const PaymentLinkButton = ({ lead }: { lead: SignupSubmission }) => {
  const { request, loading, data } = useGetSignupPaymentLink();
  const [copied, setCopied] = useState(false);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Box sx={{ display: "flex", gap: "12px" }}>
        <Button
          variant="text"
          onClick={async () => await request({ ref: lead.ref })}
          disabled={loading}
          sx={{ height: "23px" }}
        >
          Generate
        </Button>
        {data?.url && (
          <Box sx={{ display: "flex", gap: "12px" }}>
            {copied ? (
              <Typography variant="bodyHeavy" sx={{ color: "#198282" }}>
                Copied!
              </Typography>
            ) : (
              <Button
                sx={{ height: "23px" }}
                variant="text"
                onClick={() => {
                  navigator.clipboard.writeText(data.url);
                  setCopied(true);
                  setTimeout(() => {
                    setCopied(false);
                  }, 2000);
                }}
              >
                Copy to clipboard
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

interface LeadCardProps {
  lead: SignupSubmission;
  forceOpen?: boolean;
  onClose: () => void; // only relevant if forceOpen is true
  onOpen: () => void; // called when the card is opened
  refresh: () => Promise<void>;
  hideActions?: boolean;
  dateType?: "completion" | "lastActivity";
}

export default ({
  lead,
  forceOpen = false,
  onClose,
  onOpen,
  refresh,
  hideActions = false,
  dateType = "completion",
}: LeadCardProps) => {
  const isMobile = useIsMobile();
  const [convertData, setConvertData] = useState<
    [Partial<Family>, Partial<Member>[]] | null
  >(null);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [showManualAssignDialog, setShowManualAssignDialog] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(forceOpen);
  const { request: updateRequest, loading } = useUpdateSubmission();

  const handleReferrerClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!lead.referredBy) return;

      if (
        lead.referredBy.accountType === AccountType.MEMBER &&
        lead.referredBy.familyRef
      ) {
        window.open(
          `/families/${encodeURIComponent(
            lead.referredBy.familyRef,
          )}?adminView=1#tab-admin`,
          "_blank",
        );
      } else {
        window.open(
          `/tools/advisor-administration/${encodeURIComponent(
            lead.referredBy.ref,
          )}`,
          "_blank",
        );
      }
    },
    [lead.referredBy, navigate],
  );

  const handleUpdate = async (
    status: string,
    membership?: MembershipOption,
  ) => {
    if (loading) return;
    const data = emptyUpdates(lead.ref);
    data.updatedPipelineStatus = status;
    if (membership) {
      const protoMembership = new Membership({
        name: membership.name,
        supportHours: membership.supportHours,
        maxUsers: membership.maxUsers,
        amountCents: membership.amountCents,
        additionalFeatures: membership.additionalFeatures || [],
      });
      data.updatedMembership = protoMembership;
    }
    await updateRequest(data);
    await refresh();
  };

  const handleOpen = () => {
    setOpen(true);
    onOpen();
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const normalized = normalizeLeadState(lead);
  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        width: "100%",
        background: open ? "#FBFBFB" : "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "start" : "center",
          gap: "12px",
          cursor: !open ? "pointer" : undefined,
          padding: isMobile ? "12px 20px" : "16px 24px",
        }}
        onClick={() => !open && handleOpen()}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: isMobile ? "space-between" : "",
          }}
        >
          <Box
            sx={{
              flex: isMobile ? "" : leftFlex,
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              overflow: "hidden",
            }}
          >
            <Typography variant={isMobile ? "h4" : "bodyHeavy"}>
              {`${normalized.firstName} ${normalized.lastName}`}
            </Typography>

            <Typography variant="body" color="text.secondary">
              {`${normalized.shortLocation}`}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flex: isMobile ? "" : rightFlex,
              justifyContent: "space-between",
            }}
          >
            {!isMobile && getStatus(lead)}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {!isMobile && (
                <Typography variant="body">
                  {/* Formatted date */}
                  {dateType === "completion" ? (
                    <DateDisplay
                      date={new Date(Number(lead.completionSec) * 1000)}
                    />
                  ) : (
                    <DateDisplay
                      date={new Date(Number(lead.lastActivitySec) * 1000)}
                    />
                  )}
                </Typography>
              )}
              {/*<Box flex={2} display="flex" justifyContent="end">*/}
              {/*  /!*{getStatus(lead.state?.status || "active")}*!/*/}
              {/*</Box>*/}
              {forceOpen ? (
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => handleClose()}
                >
                  <X />
                </IconButton>
              ) : (
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {open ? <ChevronUp /> : <ChevronDown />}
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {isMobile && getStatus(lead)}
      </Box>
      <Collapse in={open || forceOpen} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: "#ECECEC" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            background: "white",
            padding: "0px 24px 16px",
            borderRadius: "0 0 8px 8px",
          }}
        >
          {/* Top half*/}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "24px",
              padding: "20px 0",
            }}
          >
            {/* Left side */}
            <LeadDetails lead={lead} dateType={dateType} />

            {/* Right side */}
            <Box
              sx={{
                flex: rightFlex,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {(normalized?.membershipName || normalized?.customerId) && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <Typography variant="bodyHeavy">Membership</Typography>

                    {normalized?.membershipName &&
                      normalized?.membershipHours && (
                        <Typography variant="body">
                          {normalized?.membershipName +
                            " - " +
                            normalized?.membershipHours}{" "}
                          {pluralize("hour", normalized?.membershipHours)}
                        </Typography>
                      )}

                    {normalized?.customerId ? (
                      <Link
                        href={`https://dashboard.stripe.com/customers/${normalized.customerId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "flex",
                          fontWeight: "600",
                          gap: "4px",
                          alignItems: "center",
                        }}
                      >
                        Customer
                        <ExternalLink size={14} />
                      </Link>
                    ) : undefined}
                    {normalized?.couponId ? (
                      <Box sx={{ display: "flex", gap: "4px" }}>
                        <Link
                          href={`https://dashboard.stripe.com/coupons/${normalized.couponId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: "flex",
                            fontWeight: "600",
                            gap: "4px",
                            alignItems: "center",
                          }}
                        >
                          Coupon
                          <ExternalLink size={14} />
                        </Link>
                        {normalized?.promoCode ? (
                          <Typography variant="bodyHeavy">
                            (Promo Code: {normalized.promoCode})
                          </Typography>
                        ) : undefined}
                      </Box>
                    ) : undefined}
                  </Box>
                )}
                {lead.referralType && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <Typography variant="bodyHeavy">Referred By</Typography>
                    <Typography variant="body">
                      <Link
                        onClick={handleReferrerClick}
                        sx={{
                          textDecoration: "none",
                          color: "#198282",
                          cursor: "pointer",
                        }}
                      >
                        {lead.referredBy?.firstName} {lead.referredBy?.lastName}
                      </Link>
                      {lead.referralType && ` (${lead.referralType})`}
                    </Typography>
                  </Box>
                )}
                <BasicDetail
                  label={"Consultation"}
                  horizontal={true}
                  content={
                    lead.state?.consultationEventStartSec
                      ? absoluteTime(
                          new Date(
                            Number(lead.state?.consultationEventStartSec) *
                              1000,
                          ),
                        )
                      : undefined
                  }
                />
                <BasicDetail
                  label={"Consultation host"}
                  horizontal={true}
                  content={lead.state?.consultationEventHost}
                />

                <BasicDetail
                  label={"Generate NUX payment link"}
                  horizontal={true}
                  content={<PaymentLinkButton lead={lead} />}
                />
                <LeadMetadata lead={lead} />
              </Box>

              {/* Actions */}
              {!hideActions && (
                <Box
                  display="flex"
                  flexDirection={isMobile ? "row-reverse" : "row"}
                  gap={isMobile ? "24px" : "10px"}
                  justifyContent={isMobile ? "start" : "end"}
                >
                  {lead.pipelineStatus === "signup-complete" ||
                  lead.pipelineStatus === "awaiting-match" ||
                  lead.pipelineStatus === "consultation-scheduled" ? (
                    <>
                      {lead.pipelineStatus === "consultation-scheduled" ? (
                        <DropdownMenu
                          size="small"
                          fullWidth
                          buttonContent="Update"
                          dropdownIcon={true}
                          options={[
                            {
                              title: "Needs matchmaking",
                              IconComponent: BookUser,
                              onClick: async () => {
                                setShowMembershipModal(true);
                              },
                            },
                            {
                              title: "Verbal yes",
                              IconComponent: CheckCircle,
                              onClick: async () => {
                                await handleUpdate("verbal-yes");
                              },
                            },
                            {
                              title: "Not for them",
                              IconComponent: OctagonX,
                              onClick: async () => {
                                await handleUpdate("declined");
                              },
                            },
                            {
                              title: "Maybe later",
                              IconComponent: AlarmClock,
                              onClick: async () => {
                                await handleUpdate("maybe-later");
                              },
                            },
                            {
                              title: "Canceled consultation",
                              IconComponent: CircleOff,
                              onClick: async () => {
                                await handleUpdate("consultation-cancelled");
                              },
                            },
                            {
                              title: "No show",
                              IconComponent: Ghost,
                              onClick: async () => {
                                await handleUpdate("no-show");
                              },
                            },
                            {
                              title: "Delete",
                              IconComponent: Trash2,
                              onClick: async () => {
                                await handleUpdate("deleted");
                              },
                              belowDivider: true,
                            },
                          ]}
                        />
                      ) : (
                        <Button
                          onClick={async () => await handleUpdate("deleted")}
                          disabled={loading}
                          color="error"
                          variant="text"
                          size="small"
                          sx={{
                            justifyContent: "start",
                            "&:hover": {
                              backgroundColor: "unset",
                            },
                          }}
                        >
                          Delete
                        </Button>
                      )}
                      <DropdownMenu
                        size="small"
                        fullWidth
                        buttonContent="Convert"
                        dropdownIcon={true}
                        options={[
                          {
                            title: "Assign",
                            IconComponent: BookUser,
                            onClick: async () => {
                              // Check if family exists - new flow vs old flow
                              if (lead.family) {
                                // New flow: member and family exist, use manual assignment
                                setShowManualAssignDialog(true);
                              } else {
                                // Old flow: create family first
                                const _convertData = await leadToFamily(lead);
                                setConvertData(_convertData);
                              }
                            },
                          },
                          {
                            title: "Matchmaker",
                            IconComponent: Clock,
                            onClick: async () => {
                              navigate(
                                `/tools/matchmaking/${encodeURIComponent(
                                  lead.ref,
                                )}`,
                              );
                            },
                            belowDivider: true,
                          },
                        ]}
                      />
                    </>
                  ) : (
                    <DropdownMenu
                      size="small"
                      fullWidth
                      buttonContent="Update"
                      dropdownIcon={true}
                      options={[
                        {
                          title: "Needs matchmaking",
                          IconComponent: BookUser,
                          onClick: async () => {
                            setShowMembershipModal(true);
                          },
                        },
                        ...(lead.pipelineStatus === "verbal-yes"
                          ? [
                              {
                                title: "Did not convert",
                                IconComponent: CircleOff,
                                onClick: async () => {
                                  await handleUpdate(
                                    "consultation-non-conversion",
                                  );
                                },
                              },
                            ]
                          : []),
                        {
                          title: "Delete",
                          IconComponent: Trash2,
                          onClick: async () => {
                            await handleUpdate("deleted");
                          },
                          belowDivider: true,
                        },
                      ]}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Bottom half */}
          <LeadAnswers lead={lead} />
          {open && <LeadFeed lead={lead} />}
        </Box>
      </Collapse>
      {!!convertData && (
        <AddFamilyDialog
          selectAdvisor={true}
          open={!!convertData}
          initialFamilyData={convertData?.[0]}
          initialMembers={convertData?.[1]}
          onClose={() => setConvertData(null)}
        />
      )}
      <MembershipSelectionModal
        open={showMembershipModal}
        onClose={() => setShowMembershipModal(false)}
        onSelect={async (membership) => {
          await handleUpdate("awaiting-match", membership);
        }}
      />
      <ManualAdvisorAssignDialog
        open={showManualAssignDialog}
        onClose={() => setShowManualAssignDialog(false)}
        submissionRef={lead.ref}
        leadName={`${normalized.firstName} ${normalized.lastName}`}
        onSuccess={refresh}
      />
    </Box>
  );
};
